import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MinistriesService, type InscripcionEvento, type Evento } from '../../services/ministries/ministries.service';
import { MembershipsService, type Membership } from '../../services/memberships/memberships.service';
import { NotificationToast } from '../ui/NotificationToast';
import { Users, X, Search, Trash2, UserPlus } from 'lucide-react';

interface EventoInscripcionesDrawerProps {
  eventoId: string | null;
  onClose: () => void;
}

export const EventoInscripcionesDrawer: React.FC<EventoInscripcionesDrawerProps> = ({
  eventoId,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [evento, setEvento] = useState<Evento | null>(null);
  const [inscripciones, setInscripciones] = useState<InscripcionEvento[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Membership[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMiembro, setSelectedMiembro] = useState<Membership | null>(null);
  const [notas, setNotas] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (eventoId) {
      setIsVisible(true);
      loadData();
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setEvento(null);
        setInscripciones([]);
        setSearchQuery('');
        setSearchResults([]);
        setSelectedMiembro(null);
        setNotas('');
      }, 300);
    }
  }, [eventoId]);

  const loadData = async () => {
    if (!eventoId) return;
    setIsLoading(true);
    try {
      const [eventoData, inscripcionesData] = await Promise.all([
        MinistriesService.getEvento(eventoId),
        MinistriesService.getInscripciones(eventoId),
      ]);
      setEvento(eventoData);
      setInscripciones(inscripcionesData);
    } catch (error) {
      console.error('Error cargando inscripciones:', error);
      showToast('Error', 'No se pudieron cargar las inscripciones', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotificationConfig({ title, message, type });
    setShowNotification(true);
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setSelectedMiembro(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await MembershipsService.searchMemberships(query.trim(), 1, 10);
        // Filtrar miembros ya inscritos
        const inscritosIds = new Set(inscripciones.map((i) => i.membresiaId));
        const filtered = result.data.filter((m) => !inscritosIds.has(m.id));
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error buscando miembros:', error);
      } finally {
        setIsSearching(false);
      }
    }, 400);
  }, [inscripciones]);

  const handleInscribir = async () => {
    if (!eventoId || !selectedMiembro) return;
    setIsLoading(true);
    try {
      const inscripcion = await MinistriesService.inscribirMiembro(eventoId, selectedMiembro.id, notas || undefined);
      const nombre = `${selectedMiembro.nombres} ${selectedMiembro.apellidos}`;
      if (inscripcion.estado === 'en_espera') {
        showToast('En lista de espera', `${nombre} fue agregado a la lista de espera. Se confirmará automáticamente si se libera un cupo.`, 'info');
      } else {
        showToast('¡Inscrito!', `${nombre} fue inscrito exitosamente.`, 'success');
      }
      setSelectedMiembro(null);
      setSearchQuery('');
      setSearchResults([]);
      setNotas('');
      await loadData();
    } catch (error: any) {
      showToast('Error', error.message || 'Error al inscribir miembro', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelar = async (inscripcionId: string, nombre: string) => {
    if (!eventoId) return;
    if (!confirm(`¿Cancelar inscripción de ${nombre}?`)) return;
    setIsLoading(true);
    try {
      await MinistriesService.cancelarInscripcion(eventoId, inscripcionId);
      showToast('Inscripción cancelada', `La inscripción de ${nombre} fue cancelada.`, 'success');
      await loadData();
    } catch (error: any) {
      showToast('Error', error.message || 'Error al cancelar inscripción', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const inscripcionesConfirmadas = inscripciones.filter((i) => i.estado === 'confirmada');
  const inscripcionesEspera = inscripciones.filter((i) => i.estado === 'en_espera');
  const cuposDisponibles = evento?.capacidadMaxima != null
    ? evento.capacidadMaxima - inscripcionesConfirmadas.length
    : null;
  const sinCupos = cuposDisponibles !== null && cuposDisponibles <= 0;

  if (!eventoId) return null;

  return (
    <>
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}
      <div className="fixed left-0 right-0 bottom-0 top-0 z-[70] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={handleClose}
            aria-label="Cerrar drawer"
          />
          <section
            className={`absolute right-0 top-0 h-full w-full max-w-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div
              className="h-full flex flex-col bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xs font-medium text-gray-900">Inscripciones</h2>
                  {evento && (
                    <p className="text-xs text-gray-500 mt-0.5">{evento.titulo}</p>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Cupos */}
              {evento?.requiereInscripcion && (
                <div className="px-6 py-3 bg-orange-50 border-b border-orange-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-600" />
                    <span className="text-xs text-orange-700">
                      <span className="font-medium">{inscripcionesConfirmadas.length}</span> inscritos
                    </span>
                  </div>
                  {cuposDisponibles !== null && (
                    <span className={`text-xs font-medium ${cuposDisponibles <= 0 ? 'text-red-600' : 'text-orange-700'}`}>
                      {cuposDisponibles <= 0 ? 'Sin cupos' : `${cuposDisponibles} cupos disponibles`}
                    </span>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-6">
                {/* Buscador */}
                <div className="mb-6">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Buscar miembro para inscribir</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Nombre, email o documento..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>

                  {/* Resultados de búsqueda */}
                  {searchResults.length > 0 && !selectedMiembro && (
                    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                      {searchResults.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setSelectedMiembro(m);
                            setSearchResults([]);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                        >
                          <div>
                            <p className="text-xs font-medium text-gray-900">
                              {m.nombres} {m.apellidos}
                            </p>
                            <p className="text-xs text-gray-500">{m.email}</p>
                          </div>
                          <UserPlus className="w-4 h-4 text-purple-500" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Miembro seleccionado */}
                  {selectedMiembro && (
                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-purple-900">
                          {selectedMiembro.nombres} {selectedMiembro.apellidos}
                        </p>
                        <button
                          onClick={() => {
                            setSelectedMiembro(null);
                            setSearchQuery('');
                          }}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Cambiar
                        </button>
                      </div>
                      <input
                        type="text"
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        placeholder="Notas de la inscripción (opcional)"
                        className="w-full px-3 py-2 border border-purple-200 rounded-lg text-xs focus:border-purple-500 focus:ring-purple-500/20 focus:ring-2 focus:outline-none mb-2"
                      />
                      <button
                        onClick={handleInscribir}
                        disabled={isLoading}
                        className="w-full py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading
                          ? 'Procesando...'
                          : sinCupos
                            ? 'Agregar a lista de espera'
                            : 'Confirmar inscripción'}
                      </button>
                      {sinCupos && (
                        <p className="text-[10px] text-yellow-700 mt-1.5 bg-yellow-50 border border-yellow-200 rounded p-1.5">
                          No hay cupos disponibles. El miembro entrará a la lista de espera y se confirmará automáticamente si alguien cancela.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Lista de inscripciones */}
                <div>
                  <h3 className="text-xs font-medium text-gray-800 mb-3">
                    Lista de inscritos
                    {inscripcionesConfirmadas.length > 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                        {inscripcionesConfirmadas.length}
                      </span>
                    )}
                  </h3>
                  {isLoading && inscripciones.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full" />
                    </div>
                  ) : inscripciones.filter((i) => i.estado !== 'en_espera').length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-xs">
                      No hay inscripciones registradas
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {inscripciones.filter((i) => i.estado !== 'en_espera').map((insc) => (
                        <div
                          key={insc.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            insc.estado === 'confirmada'
                              ? 'bg-white border-gray-200'
                              : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-xs font-medium text-purple-700">
                                {(insc.membresia?.nombres?.[0] || 'U')}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-900">
                                {insc.membresia?.nombres} {insc.membresia?.apellidos}
                              </p>
                              <p className="text-xs text-gray-500">
                                {insc.estado === 'confirmada' ? 'Confirmada' : 'Cancelada'}
                              </p>
                            </div>
                          </div>
                          {insc.estado === 'confirmada' && (
                            <button
                              onClick={() => handleCancelar(insc.id, `${insc.membresia?.nombres} ${insc.membresia?.apellidos}`)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Cancelar inscripción"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista de espera */}
                  {inscripcionesEspera.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-xs font-medium text-gray-800 mb-1">
                        Lista de espera
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                          {inscripcionesEspera.length}
                        </span>
                      </h3>
                      <p className="text-[10px] text-gray-400 mb-3">
                        Se confirma automáticamente en orden de llegada si se libera un cupo.
                      </p>
                      <div className="space-y-2">
                        {inscripcionesEspera.map((insc, idx) => (
                          <div
                            key={insc.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-yellow-50 border-yellow-200"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-yellow-200 flex items-center justify-center text-[10px] font-semibold text-yellow-800">
                                {idx + 1}
                              </span>
                              <div>
                                <p className="text-xs font-medium text-gray-900">
                                  {insc.membresia?.nombres} {insc.membresia?.apellidos}
                                </p>
                                <p className="text-xs text-yellow-700">En espera</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleCancelar(insc.id, `${insc.membresia?.nombres} ${insc.membresia?.apellidos}`)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Retirar de la lista de espera"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
